import { useMemo, useState } from "react";
import { IconButton, InputBase, Tooltip } from "@mui/material";
import { Helmet } from "react-helmet-async";
import moment from "moment";
import secureLocalStorage from "react-secure-storage";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Logo from "../assets/Logo.png";
import {
  BANK_NAME,
  BRAND_THEME,
  SUITE_NAME,
  SUITE_TAGLINE,
  VENDOR_NAME,
  pageTitle,
} from "../brand";
import { ADMIN_CONSOLE, SYSTEMS } from "./systems";
import "./style.css";

const RECENT_SYSTEMS_KEY = "cbx_recent_systems";

const readRecentSystems = () => {
  try {
    const raw = localStorage.getItem(RECENT_SYSTEMS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (error) {
    return [];
  }
};

const writeRecentSystem = (tab) => {
  try {
    const next = [
      { key: tab.key, openedAt: new Date().toISOString() },
      ...readRecentSystems().filter((item) => item.key !== tab.key),
    ].slice(0, 4);
    localStorage.setItem(RECENT_SYSTEMS_KEY, JSON.stringify(next));
  } catch (error) {
    // private window or blocked storage: just don't remember it
  }
};

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

function SystemCard({ system, locked, onOpen, onOpenWindow }) {
  const { Icon } = system;

  if (locked) {
    return (
      <div className="launch-card launch-card--locked">
        <span className="launch-icon launch-icon--locked">
          <Icon />
        </span>
        <div className="launch-card-body">
          <span className="launch-card-name">{system.name}</span>
          <span className="launch-card-details">{system.details}</span>
        </div>
        <Tooltip title="Ask your administrator for access">
          <LockOutlinedIcon className="launch-lock" fontSize="small" />
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      className={`launch-card launch-card--${system.accent}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(system)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(system);
        }
      }}
    >
      <span className="launch-icon">
        <Icon />
      </span>
      <div className="launch-card-body">
        <span className="launch-card-name">{system.name}</span>
        <span className="launch-card-details">{system.details}</span>
      </div>
      <div className="launch-card-actions">
        <Tooltip title="Open in a new browser tab">
          <IconButton
            size="small"
            aria-label={`Open ${system.name} in a new browser tab`}
            onClick={(event) => {
              event.stopPropagation();
              onOpenWindow(system);
            }}
          >
            <OpenInNewRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <ArrowForwardRoundedIcon className="launch-card-go" fontSize="small" />
      </div>
    </div>
  );
}

export default function Apps({ onOpenSystem }) {
  const rawUser = secureLocalStorage.getItem("profile");
  const user = rawUser ? JSON.parse(rawUser) : null;
  const results = user?.results || {};

  const isAdmin = String(results.userRole || "").toLowerCase() === "admin";
  const [query, setQuery] = useState("");
  const [showLocked, setShowLocked] = useState(false);
  const [recent, setRecent] = useState(readRecentSystems);

  // A system opens for admins, for everyone when it names no permission,
  // or when any user field it names is set to something other than
  // "Restricted". adminOnly systems are for admins alone.
  const hasAccess = (system) => {
    if (isAdmin) return true;
    if (system.adminOnly) return false;
    if (!system.permission) return true;
    return [].concat(system.permission).some((field) => {
      const value = results[field];
      return Boolean(value) && value !== "Restricted";
    });
  };

  const available = SYSTEMS.filter(hasAccess);
  const locked = SYSTEMS.filter((system) => !hasAccess(system));

  const search = query.trim().toLowerCase();
  const matches = (system) =>
    !search ||
    system.name.toLowerCase().includes(search) ||
    system.details.toLowerCase().includes(search);

  const recentSystems = useMemo(
    () =>
      recent
        .map((item) => ({
          ...item,
          system: available.find((system) => system.key === item.key),
        }))
        .filter((item) => item.system),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recent, available.length],
  );

  const remember = (system) => {
    writeRecentSystem(system);
    setRecent(readRecentSystems());
  };

  const open = (system) => {
    remember(system);
    if (typeof onOpenSystem === "function") onOpenSystem(system);
    else window.open(system.to, "_blank", "noopener,noreferrer");
  };

  const openWindow = (system) => {
    remember(system);
    window.open(system.to, "_blank", "noopener,noreferrer");
  };

  const shownAvailable = available.filter(matches);
  const shownLocked = locked.filter(matches);

  return (
    <div className="launcher">
      <Helmet>
        <title>{pageTitle("Systems")}</title>
      </Helmet>

      <header
        className="launcher-brand"
        style={{
          "--brand-from": BRAND_THEME.from,
          "--brand-to": BRAND_THEME.to,
          "--brand-stripe-1": BRAND_THEME.stripe1,
          "--brand-stripe-2": BRAND_THEME.stripe2,
        }}
      >
        <div className="launcher-brand-main">
          <span
            className={`launcher-brand-logo launcher-brand-logo--${BRAND_THEME.logoFit}`}
          >
            <img src={Logo} alt={`${BANK_NAME} logo`} />
          </span>
          <div className="launcher-brand-text">
            <h1>{BANK_NAME}</h1>
            <p>
              <strong>{SUITE_NAME}</strong>
              <span aria-hidden="true"> · </span>
              {SUITE_TAGLINE}
            </p>
          </div>
        </div>

        <div className="launcher-brand-user">
          <p className="launcher-date">{moment().format("dddd, D MMMM YYYY")}</p>
          <p className="launcher-greeting">
            {greeting()}
            {results.firstName ? `, ${results.firstName}` : ""}
          </p>
          <div className="launcher-chips">
            <span>{results.branch || "No branch set"}</span>
            <span>{results.userRole || "User"}</span>
          </div>
        </div>
      </header>

      {!search && recentSystems.length > 0 && (
        <section className="launcher-section">
          <h2 className="launcher-section-title">
            <HistoryRoundedIcon fontSize="small" /> Recently opened
          </h2>
          <div className="launcher-recent">
            {recentSystems.map(({ system, openedAt }) => (
              <button
                key={system.key}
                type="button"
                className={`launcher-recent-item launch-card--${system.accent}`}
                onClick={() => open(system)}
              >
                <span className="launch-icon launch-icon--small">
                  <system.Icon />
                </span>
                <span className="launcher-recent-text">
                  <strong>{system.name}</strong>
                  <small>{moment(openedAt).fromNow()}</small>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="launcher-section">
        <div className="launcher-section-head">
          <h2 className="launcher-section-title">
            Your systems{" "}
            <span className="launcher-count">{available.length}</span>
          </h2>
          <label className="launcher-search">
            <SearchRoundedIcon fontSize="small" />
            <InputBase
              placeholder="Search systems"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              inputProps={{ "aria-label": "Search systems" }}
              fullWidth
            />
          </label>
        </div>
        {shownAvailable.length > 0 ? (
          <div className="launcher-grid">
            {shownAvailable.map((system) => (
              <SystemCard
                key={system.key}
                system={system}
                onOpen={open}
                onOpenWindow={openWindow}
              />
            ))}
          </div>
        ) : (
          <p className="launcher-empty">
            {search
              ? `No system you can open matches "${query}".`
              : "You don't have access to any system yet. Ask your administrator."}
          </p>
        )}
      </section>

      {isAdmin && !search && (
        <section className="launcher-section">
          <h2 className="launcher-section-title">Administration</h2>
          <div className="launcher-grid">
            <SystemCard
              system={{
                ...ADMIN_CONSOLE,
                Icon: AdminPanelSettingsRoundedIcon,
                accent: "slate",
              }}
              onOpen={open}
              onOpenWindow={openWindow}
            />
          </div>
        </section>
      )}

      {shownLocked.length > 0 && (
        <section className="launcher-section">
          <button
            type="button"
            className="launcher-locked-toggle"
            onClick={() => setShowLocked((value) => !value)}
            aria-expanded={showLocked || Boolean(search)}
          >
            <LockOutlinedIcon fontSize="small" />
            Not available to you ({shownLocked.length})
            <ExpandMoreRoundedIcon
              fontSize="small"
              className={showLocked || search ? "is-open" : ""}
            />
          </button>
          {(showLocked || search) && (
            <>
              <p className="launcher-hint">
                Ask your administrator if you need any of these.
              </p>
              <div className="launcher-grid">
                {shownLocked.map((system) => (
                  <SystemCard key={system.key} system={system} locked />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      <footer className="launcher-footer">
        <span>
          Authorised use only. Activity in these systems is logged and may be
          audited.
        </span>
        <span>
          {SUITE_NAME} · Powered by {VENDOR_NAME}
        </span>
      </footer>
    </div>
  );
}
